"use client";

import { motion } from "framer-motion";
import { useState } from "react";
import { Plus } from "lucide-react";
/* ================= DATA ================= */

const PROGRAMS = [
  {
    id: "bootcamp",
    title: "BroadBoy Mountain Boot Camp",
    subtitle: "7-Day Foundation Program",
    funding: "sponsored",
    description:
      "A seven-day foundational climbing and personal development program designed to shape stronger, smarter, and more responsible climbers. This boot camp introduces young athletes and local porters to the real culture of mountaineering, with emphasis on discipline, teamwork, and survival in demanding mountain environments.",
    items: [
      "3 days physical training, 3 days technical climbing, 1 day recovery & review",
      "Survival skills, mountain ethics, hygiene & safety awareness",
      "Team building, discipline, and routine development",
      "Mental strength, confidence, and decision-making under pressure",
      "Basic rescue techniques and emergency response awareness",
      "High-altitude awareness and mountain safety fundamentals",
    ],
  },
  {
  id: "technical",
  title: "Hoper Glacier Mountaineering Course",
  subtitle: "Level One & Two Training Program",
  funding: "paid",
  description:
    "The Hoper Glacier Mountaineering Course is a structured Level One & Two training program designed to build strong technical and safety foundations for aspiring mountaineers. Conducted in a real glacier environment, the course focuses on practical skills required for high-altitude climbing, glacier travel, and expedition preparedness. Participants are trained to operate safely, efficiently, and responsibly in mountain terrain while developing confidence and decision-making skills.",
  items: [
    "Core mountaineering techniques and rope systems",
    "Climbing knots, anchors, and belay methods on ice and rock",
    "Glacier travel, ice climbing, and self-rescue skills",
    "Use of crampons, ice axes, and mountaineering equipment",
    "Emergency response awareness, including altitude-related illnesses",
    "Leadership, client care, and mountain ethics",
    "Evaluation and certification upon course completion",
  ],
}

];

/* ================= COMPONENT ================= */

export default function TrainingPrograms() {
  const [active, setActive] = useState(0);

  return (
    <section className="bg-gray-900/10 backdrop-blur-lg text-white py-32 px-6 md:px-12">
      <div className="max-w-6xl mx-auto">

        {/* ================= HEADER ================= */}
        <div className="max-w-3xl mb-24">
          <span className="inline-block mb-6 text-[11px] uppercase tracking-[0.45em] text-neutral-400">
            BroadBoy Climbing Academy
          </span>

          <h2 className="text-4xl md:text-5xl font-semibold leading-[1.1] tracking-tight">
            Training programs
            <br />
            <span className="text-neutral-400">built for real mountains</span>
          </h2>

          <p className="mt-6 max-w-2xl text-[15px] leading-relaxed text-neutral-300">
            BBCA training programs prepare climbers for real-world environments.
            Each program emphasizes safety, discipline, and long-term growth,
            never shortcuts.
          </p>
        </div>

        {/* ================= PROGRAMS ================= */}
        <div className="relative divide-y divide-white/10">
          {PROGRAMS.map((program, i) => {
            const isActive = active === i;

            return (
              <motion.div
                key={program.id}
                layout
                onClick={() => setActive(i)}
                transition={{ layout: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } }}
                className="relative cursor-pointer py-12"
              >
                {/* ROW */}
                <div className="grid grid-cols-[auto_1fr_auto] gap-8 md:gap-14">

                  {/* INDEX */}
                  <div>
                    <span
                      className={`
                        block text-5xl md:text-6xl font-semibold tracking-tight
                        ${isActive ? "text-white" : "text-white/20"}
                        transition-colors
                      `}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>

                  {/* CONTENT */}
                  <div className="space-y-4">

                    {/* META */}
                    <div className="flex items-center gap-4">
                      <span className="text-xs uppercase tracking-[0.35em] text-neutral-400">
                        {program.subtitle}
                      </span>

                        {/* FUNDING BADGE */}
                        {program.funding === "sponsored" && (
                          <span className="rounded-full border border-blue-400/30 bg-blue-400/10 px-3 py-1 text-[10px] uppercase tracking-widest text-emerald-300">
                            Sponsored Program
                          </span>
                        )}

                         {program.funding === "paid" && (
                          <span className="rounded-full border border-blue-400/30 bg-blue-400/10 px-3 py-1 text-[10px] uppercase tracking-widest text-emerald-300">
                            Paid Program
                          </span>
                        )}

                      {!isActive && (
                        <span className="h-px w-12 bg-white/20" />
                      )}
                    </div>

                    {/* TITLE */}
                    <h3
                      className={`
                        text-2xl md:text-3xl font-semibold tracking-tight
                        ${isActive ? "text-white" : "text-white/70"}
                        transition-colors
                      `}
                    >
                      {program.title}
                    </h3>

                    {/* EXPANDED CONTENT */}
                    {isActive && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                        className="pt-6 space-y-10"
                      >
                        <p className="max-w-3xl text-lg leading-relaxed text-neutral-300">
                          {program.description}
                        </p>

                        <div className="space-y-4">
                          {program.items.map((item, idx) => (
                            <div key={idx} className="flex items-start gap-6">
                              <span className="mt-2 h-1.5 w-1.5 rounded-full bg-white" />
                              <p className="text-[15px] leading-relaxed text-neutral-300">
                                {item}
                              </p>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </div>

                  {/* EXPAND INDICATOR */}
                  <div className="flex items-start pt-2">
                    <motion.span
                      initial={false}
                      animate={{
                        rotate: isActive ? 45 : 0,
                        opacity: isActive ? 0 : 0.6,
                      }}
                      transition={{ duration: 0.25, ease: "easeOut" }}
                      className="text-sm font-light text-white"
                    >
                          <Plus size={18} className="text-white" />
                    </motion.span>
                  </div>

                </div>
              </motion.div>
            );
          })}
        </div>

      </div>
    </section>
  );
}
